import {
  Typography,
  TableContainer,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  tableCellClasses,
  styled,
  useTheme,
} from '@mui/material';
import { Box } from '@mui/system';
import React from 'react';
import {
  PanickyTypography,
  PassableTypography,
  PoisedTypography,
  PristineTypography,
} from '../room/game/player-input/PrecisionGraphic';
import { Profile } from '@par/shared/types/player';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.root}`]: {
    textAlign: 'center',
  },
}));

type PropTypes = {
  viewProfile: Profile;
};

export const Precision = ({ viewProfile }: PropTypes) => {
  const {
    precision,
    totalGamesPlayedBeforeFuzzyPenaltyLaunch,
    questionsSeenCount,
  } = viewProfile;

  const theme = useTheme();

  const calcPer100 = (precisionCount: number, questionsSeenCount: number) => {
    if (!precisionCount) return '';
    const val =
      (precisionCount /
        (questionsSeenCount -
          (totalGamesPlayedBeforeFuzzyPenaltyLaunch || 0) * 10)) *
      100;
    return `${val.toFixed(1)}`;
  };

  return (
    <>
      <Typography
        variant="h5"
        mb={2}
        color={'inherit'}
        sx={{
          textShadow:
            theme.palette.mode === 'dark'
              ? `0 0 0px ${theme.palette.secondary.main}, 0 0 0px ${theme.palette.secondary.main}, 0px 0px 16px ${theme.palette.secondary.main}`
              : 'none',
        }}
      >
        Precision
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small" aria-label="simple table">
          <TableBody>
            <TableRow>
              <TableCell></TableCell>
              <StyledTableCell>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      color: theme.palette.success.main,
                    }}
                  >
                    Putts
                  </Typography>
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box
                  sx={{
                    padding: '5px',
                    border: '1px solid orange',
                    borderRadius: 4,
                  }}
                >
                  <PanickyTypography variant="h5">Panicky</PanickyTypography>
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box
                  sx={{
                    padding: '3px',
                    border: '1px solid',
                    borderColor: 'cadetblue',
                  }}
                >
                  <PassableTypography>Passable</PassableTypography>
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box>
                  <PoisedTypography variant="h4">Poised</PoisedTypography>
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box
                  sx={{
                    padding: '3px',
                    backgroundColor: 'lightgray',
                    borderRadius: 4,
                    boxShadow: `
          inset 0 0 50px #f1f1f1,      /* inner white */
          inset 20px 0 80px #f0f,   /* inner left magenta short */
          inset -20px 0 80px #0ff,  /* inner right cyan short */
          inset 20px 0 300px #f0f,  /* inner left magenta broad */
          inset -20px 0 300px #0ff, /* inner right cyan broad */
          0 0 5px #f1f1f1,            /* outer white */
          -1px 0 20px #f0f,        /* outer left magenta */
          1px 0 20px #0ff;         /* outer right cyan */`,
                  }}
                >
                  <PristineTypography variant="h5">Pristine</PristineTypography>
                </Box>
              </StyledTableCell>
            </TableRow>
            <TableRow>
              <TableCell>Lifetime</TableCell>
              <StyledTableCell sx={{ color: theme.palette.success.main }}>
                {precision['putt']}
              </StyledTableCell>
              <StyledTableCell sx={{ color: 'orange' }}>
                {precision['panicky']}
              </StyledTableCell>
              <StyledTableCell sx={{ color: '#64faff' }}>
                {precision['passable']}
              </StyledTableCell>
              <StyledTableCell sx={{ color: 'red' }}>
                {precision['poised']}
              </StyledTableCell>
              <StyledTableCell sx={{ color: '#f1b7f1' }}>
                {precision['pristine']}
              </StyledTableCell>
            </TableRow>
            <TableRow
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell>Per 100 Qs</TableCell>
              <StyledTableCell sx={{ color: theme.palette.success.main }}>
                {calcPer100(precision['putt'], questionsSeenCount)}
              </StyledTableCell>
              <StyledTableCell sx={{ color: 'orange' }}>
                {calcPer100(precision['panicky'], questionsSeenCount)}
              </StyledTableCell>
              <StyledTableCell sx={{ color: '#64faff' }}>
                {calcPer100(precision['passable'], questionsSeenCount)}
              </StyledTableCell>
              <StyledTableCell sx={{ color: 'red' }}>
                {calcPer100(precision['poised'], questionsSeenCount)}
              </StyledTableCell>
              <StyledTableCell sx={{ color: '#f1b7f1' }}>
                {calcPer100(precision['pristine'], questionsSeenCount)}
              </StyledTableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
